import { z } from "zod/v4";

import {
  AgenticLogicShippingContextDescriptions,
  AgenticLogicShippingDeliveryDescriptions,
  AgenticLogicShippingDeliveryOptionDescriptions,
  AgenticLogicShippingTierDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicShippingStateSchema } from "./vocab";

export const AgenticLogicShippingTierSchema = z
  .object({
    threshold: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicShippingTierDescriptions.threshold),
    label: z.string().describe(AgenticLogicShippingTierDescriptions.label),
  })
  .describe(AgenticLogicShippingTierDescriptions._schema);

export type AgenticLogicShippingTier = z.infer<
  typeof AgenticLogicShippingTierSchema
>;

export const AgenticLogicShippingDeliveryOptionSchema = z
  .object({
    title: z
      .string()
      .optional()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.title),
    isPickup: z
      .boolean()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.isPickup),
    selected: z
      .boolean()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.selected),
    cost: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.cost),
    currency: z
      .string()
      .optional()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.currency),
    carrierName: z
      .string()
      .optional()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.carrierName),
    transitDays: z
      .object({
        min: z.number().int().nonnegative().optional(),
        max: z.number().int().nonnegative().optional(),
      })
      .optional()
      .describe(AgenticLogicShippingDeliveryOptionDescriptions.transitDays),
  })
  .describe(AgenticLogicShippingDeliveryOptionDescriptions._schema);

export type AgenticLogicShippingDeliveryOption = z.infer<
  typeof AgenticLogicShippingDeliveryOptionSchema
>;

/**
 * Observed checkout delivery facts, projected from the PII-minimal delivery
 * signals the extension sends. Same guarantee as its source: option facts
 * only — no address key may ever be added here.
 */
export const AgenticLogicShippingDeliverySchema = z
  .object({
    isLocalPickup: z
      .boolean()
      .describe(AgenticLogicShippingDeliveryDescriptions.isLocalPickup),
    selected: AgenticLogicShippingDeliveryOptionSchema.optional().describe(
      AgenticLogicShippingDeliveryDescriptions.selected
    ),
    options: z
      .array(AgenticLogicShippingDeliveryOptionSchema)
      .describe(AgenticLogicShippingDeliveryDescriptions.options),
  })
  .describe(AgenticLogicShippingDeliveryDescriptions._schema);

export type AgenticLogicShippingDelivery = z.infer<
  typeof AgenticLogicShippingDeliverySchema
>;

export const AgenticLogicShippingContextSchema = z
  .object({
    state: AgenticLogicShippingStateSchema.describe(
      AgenticLogicShippingContextDescriptions.state
    ),
    currency: z
      .string()
      .describe(AgenticLogicShippingContextDescriptions.currency),
    freeShippingThreshold: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicShippingContextDescriptions.freeShippingThreshold),
    remainingToFreeShipping: z
      .number()
      .int()
      .nonnegative()
      .nullish()
      .describe(
        AgenticLogicShippingContextDescriptions.remainingToFreeShipping
      ),
    tiers: z
      .array(AgenticLogicShippingTierSchema)
      .optional()
      .describe(AgenticLogicShippingContextDescriptions.tiers),
    delivery: AgenticLogicShippingDeliverySchema.optional().describe(
      AgenticLogicShippingContextDescriptions.delivery
    ),
  })
  .describe(AgenticLogicShippingContextDescriptions._schema);

export type AgenticLogicShippingContext = z.infer<
  typeof AgenticLogicShippingContextSchema
>;

assertDescriptions<
  typeof AgenticLogicShippingTierDescriptions,
  AgenticLogicShippingTier
>();
assertDescriptions<
  typeof AgenticLogicShippingDeliveryOptionDescriptions,
  AgenticLogicShippingDeliveryOption
>();
assertDescriptions<
  typeof AgenticLogicShippingDeliveryDescriptions,
  AgenticLogicShippingDelivery
>();
assertDescriptions<
  typeof AgenticLogicShippingContextDescriptions,
  AgenticLogicShippingContext
>();
